import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { KnowledgeOrbit } from './components/KnowledgeOrbit';

interface WhyCard {
  /** i18n key suffix under pages.about.why.cards. */
  key: string;
  icon: ReactNode;
  /** Card background: white + soft accent corner glow / accent-tinted navy. */
  bgClass: string;
  /** Icon chip: soft accent tint with a readable mid-tone glyph per theme. */
  chipClass: string;
  /** Accent text (emphasis): the card's mid-tone in light, pastel on navy. */
  accentText: string;
  /** Accent bullet dot: mid-tone in light, pastel on navy. */
  accentDot: string;
}

const CARDS: WhyCard[] = [
  {
    key: 'people',
    accentText: 'text-[#B18E1E] marketing-dark:text-[#FDE47A]',
    accentDot: 'bg-[#B18E1E] marketing-dark:bg-[#FDE47A]',
    chipClass:
      'bg-[rgba(247,203,45,0.16)] text-[#B18E1E] marketing-dark:bg-[rgba(247,203,45,0.13)] marketing-dark:text-[#FDE47A]',
    bgClass: [
      'bg-white bg-[image:radial-gradient(46%_60%_at_92%_6%,rgba(247,203,45,0.22)_0%,transparent_62%)]',
      'marketing-dark:bg-[image:radial-gradient(125%_130%_at_88%_4%,rgba(247,203,45,0.15)_0%,transparent_52%),linear-gradient(160deg,#13234f,#0c1838)]',
    ].join(' '),
    icon: (
      <>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
        <path d="M16.5 5.2a3.2 3.2 0 0 1 0 5.6M18 20a6.2 6.2 0 0 0-3-5.3" />
      </>
    ),
  },
  {
    key: 'companies',
    accentText: 'text-mf-blue marketing-dark:text-[#7fa8ff]',
    accentDot: 'bg-mf-blue marketing-dark:bg-[#7fa8ff]',
    chipClass:
      'bg-[#EBF0F7] text-mf-blue marketing-dark:bg-[rgba(127,168,255,0.13)] marketing-dark:text-[#7fa8ff]',
    bgClass: [
      'bg-white bg-[image:radial-gradient(46%_60%_at_92%_6%,rgba(189,222,255,0.5)_0%,transparent_62%)]',
      'marketing-dark:bg-[image:radial-gradient(125%_130%_at_88%_4%,rgba(91,141,255,0.24)_0%,transparent_54%),linear-gradient(160deg,#13234f,#0c1838)]',
    ].join(' '),
    icon: (
      <>
        <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
        <path d="M9 9v.01M9 13v.01M9 17v.01" />
      </>
    ),
  },
  {
    key: 'agents',
    accentText: 'text-[#2BA471] marketing-dark:text-[#6ed7a6]',
    accentDot: 'bg-[#2BA471] marketing-dark:bg-[#6ed7a6]',
    chipClass:
      'bg-[rgba(110,215,166,0.18)] text-[#2BA471] marketing-dark:bg-[rgba(110,215,166,0.14)] marketing-dark:text-[#6ed7a6]',
    bgClass: [
      'bg-white bg-[image:radial-gradient(46%_60%_at_92%_6%,rgba(110,215,166,0.28)_0%,transparent_62%)]',
      'marketing-dark:bg-[image:radial-gradient(125%_130%_at_88%_4%,rgba(31,140,95,0.30)_0%,transparent_54%),linear-gradient(160deg,#0f2545,#0c1838)]',
    ].join(' '),
    icon: (
      <>
        <path d="M12 4V2" />
        <circle cx="12" cy="3" r="1" />
        <rect x="5" y="7" width="14" height="11" rx="3.2" />
        <circle cx="9.4" cy="12.2" r="1.2" />
        <circle cx="14.6" cy="12.2" r="1.2" />
        <path d="M3 13v3M21 13v3" />
      </>
    ),
  },
];

// Bullet key suffixes per card (pages.about.why.cards.<key>.<bullet>).
const BULLET_KEYS = ['bullet1', 'bullet2', 'bullet3'];

export const Section21 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>{t('pages.about.why.eyebrow')}</div>
          <h2 className={styles.title}>
            {t.rich('pages.about.why.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
              br: () => <br />,
            })}
          </h2>
          <p className={styles.lead}>{t('pages.about.why.lead')}</p>
          <KnowledgeOrbit />
        </div>

        <div className={styles.grid}>
          {CARDS.map((card) => (
            <div key={card.key} className={`${styles.card} ${card.bgClass}`}>
              {/* Light-only: dot grid fading in from the glow corner. */}
              <div className={styles.cardGrid} aria-hidden="true" />
              <div className={styles.cardHead}>
                <span className={`${styles.cardIcon} ${card.chipClass}`}>
                  <svg viewBox="0 0 24 24" className={styles.glyph}>
                    {card.icon}
                  </svg>
                </span>
                <h3 className={styles.cardTitle}>
                  {t(`pages.about.why.cards.${card.key}.title`)}
                </h3>
              </div>
              <ul className={styles.list}>
                {BULLET_KEYS.map((bulletKey) => (
                  <li key={bulletKey} className={styles.bullet}>
                    <span className={`${styles.dot} ${card.accentDot}`} />
                    {t(`pages.about.why.cards.${card.key}.${bulletKey}`)}
                  </li>
                ))}
              </ul>
              <div className={styles.core}>
                {t('pages.about.why.corePrefix')}{' '}
                <b className={card.accentText}>
                  &ldquo;{t(`pages.about.why.cards.${card.key}.core`)}&rdquo;
                </b>
              </div>
            </div>
          ))}
        </div>

        <p className={styles.enemy}>
          {t.rich('pages.about.why.enemy', {
            highlight: (chunks) => (
              <span className={styles.enemyHighlight}>{chunks}</span>
            ),
          })}
        </p>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5 text-center',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto max-w-[760px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'mx-auto font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-[18px] max-w-[620px] text-lg leading-[1.65] text-mf-gray-600 marketing-dark:text-white/66',
  grid: 'mt-[46px] grid grid-cols-1 gap-[22px] text-left lg:grid-cols-3',
  card: [
    'relative overflow-hidden rounded-[20px] border px-8 py-[30px] transition-transform duration-200 hover:-translate-y-1.5',
    'border-[#F0F0F0] hover:border-[#DADADA]',
    'marketing-dark:border-white/10 marketing-dark:hover:border-white/20',
  ].join(' '),
  cardGrid: [
    'pointer-events-none absolute inset-0 marketing-dark:hidden',
    'bg-[radial-gradient(circle,#DADADA_1px,transparent_1.4px)] bg-[length:28px_28px]',
    '[mask-image:radial-gradient(70%_90%_at_88%_10%,#000_0%,transparent_70%)]',
  ].join(' '),
  cardHead: 'relative z-[2] mb-[18px] flex items-center gap-[13px]',
  cardIcon:
    'flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-[13px]',
  glyph: 'h-[23px] w-[23px] fill-none stroke-current stroke-[1.7]',
  cardTitle:
    'font-inter text-[19px] font-bold text-mf-off-black-600 marketing-dark:text-white',
  list: 'relative z-[2] flex list-none flex-col gap-3 p-0',
  bullet:
    'flex gap-[11px] text-[15px] leading-[1.55] text-mf-gray-600 marketing-dark:text-white/64',
  dot: 'mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full',
  core: [
    'relative z-[2] mt-[18px] border-t pt-4 text-[14.5px] font-medium leading-[1.5]',
    'border-[#F0F0F0] text-mf-off-black-500',
    'marketing-dark:border-white/10 marketing-dark:text-white/85',
  ].join(' '),
  enemy:
    'mx-auto mt-12 max-w-[780px] font-inter text-[26px] font-extrabold leading-[1.35] tracking-[-0.01em] text-mf-off-black-600 marketing-dark:text-white sm:text-[30px]',
  enemyHighlight: 'text-mf-blue marketing-dark:text-mf-coral',
};
